import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import cartService from "../../db/cart-service";
import paymentService from "../../db/payment-service";
import orderService from "../../db/order-service";
import customerService from "../../db/customer-service";
import brand from "../../brand.svg";
import { currencySymbols, formatPrice as utilFormatPrice, detectUserCountry } from "../../utils/currency";
import "./ShoppingCart.scss";

const ShoppingCart = (props) => {
  const [cartProducts, setCartProducts] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [showAddressSelector, setShowAddressSelector] = useState(false);
  const [deliveryTimeSlot, setDeliveryTimeSlot] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [savedAddress, setSavedAddress] = useState("");
  const [pinCodeError, setPinCodeError] = useState("");
  const [dateError, setDateError] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(true);
  const [showInvoice, setShowInvoice] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [userCountry, setUserCountry] = useState('US');
  const invoiceRef = useRef(null);
  const navigate = useNavigate();
  
  const customerId = props.customerId || sessionStorage.getItem("customerId");
  const isAuthenticated = sessionStorage.getItem("isUserAuthenticated") === "true";
  const standalone = props.standalone || false;

  // Currency symbols imported from utility

  // Calculate next day delivery date
  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setDeliveryDate(tomorrow.toISOString().split('T')[0]);
  }, []);

  // Detect user's country using utility function
  useEffect(() => {
    const initCountry = async () => {
      const country = await detectUserCountry();
      setUserCountry(country);
    };
    
    initCountry();
  }, []);

  useEffect(() => {
    const fetchCart = async () => {
      setLoading(true);
      try {
        if (customerId) {
          const [cart, customerAddresses] = await Promise.all([
            cartService.getCart(customerId),
            customerService.getCustomerAddresses(customerId)
          ]);

          setCartProducts(cart);
          setAddresses(customerAddresses);

          const defaultAddress = customerAddresses.find(addr => addr.isDefault);
          if (defaultAddress) {
            setSelectedAddress(defaultAddress);
            setSavedAddress(`${defaultAddress.fullName}, ${defaultAddress.addressLine1}, ${defaultAddress.city}, ${defaultAddress.state} - ${defaultAddress.pincode}`);
          }
        }
      } catch (err) {
        console.error("Error fetching cart:", err);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) fetchCart();
  }, [customerId, isAuthenticated]);

  // Format price based on user's country
  const formatPrice = (price) => {
    return utilFormatPrice(price, userCountry);
  };

  const removeProduct = async (productId) => {
    try {
      await cartService.removeFromCart(productId, customerId);
      
      let updatedCartList = cartProducts.filter(
        (product) => product.productId !== productId
      );
      setCartProducts(updatedCartList);
      
      // Dispatch cart updated event
      const event = new CustomEvent('cartUpdated');
      window.dispatchEvent(event);
    } catch (err) {
      console.log("Error removing product:", err);
    }
  };

  const validatePinCode = (pinCode) => {
    if (pinCode !== '500019') {
      setPinCodeError('Delivery only available to pincode 500019');
      return false;
    }
    setPinCodeError('');
    return true;
  };

  const handleAddressSelect = (address) => {
    setSelectedAddress(address);
    setSavedAddress(`${address.fullName}, ${address.addressLine1}, ${address.city}, ${address.state} - ${address.pincode}`);
    setShowAddressSelector(false);
  };

  const generateInvoice = () => {
    if (!selectedAddress) {
      alert("Please select a delivery address");
      return;
    }
    
    if (!deliveryTimeSlot) {
      alert("Please select a delivery time slot");
      return;
    }
    

    if (!validatePinCode(selectedAddress.pincode)) {
      alert("Delivery only available to pincode 500019");
      return;
    }
    
    // Calculate subtotal
    const subtotal = cartProducts.reduce((sum, item) => sum + item.price * item.quantity, 0);
    
    // Check minimum order amount
    if (subtotal < 100) {
      const modal = document.createElement('div');
      modal.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);display:flex;align-items:center;justify-content:center;z-index:1000';
      modal.innerHTML = `
        <div style="background:white;padding:30px;border-radius:8px;text-align:center;max-width:400px;margin:20px">
          <h3 style="color:#d32f2f;margin-bottom:15px">Minimum Order Required</h3>
          <p style="margin-bottom:20px">Minimum order amount should be ₹100 or more.</p>
          <p style="margin-bottom:20px">Current cart total: ₹${subtotal}</p>
          <button onclick="this.parentElement.parentElement.remove()" style="padding:10px 20px;background:#133c0b;color:white;border:none;border-radius:4px;cursor:pointer">OK</button>
        </div>
      `;
      document.body.appendChild(modal);
      return;
    }
    
    // Always use INR for Razorpay
    const currencyCode = 'INR';
    
    // Add convenience fee of ₹50 for orders above ₹100
    const convenienceFee = subtotal > 100 ? 50 : 0;
    const total = subtotal + convenienceFee;
    
    const invoiceData = {
      invoiceNumber: 'INV-' + Date.now(),
      date: new Date().toLocaleString(),
      customerName: sessionStorage.getItem("customerName") || "Customer",
      customerId: customerId,
      address: `${selectedAddress.fullName}, ${selectedAddress.addressLine1}, ${selectedAddress.addressLine2 ? selectedAddress.addressLine2 + ', ' : ''}${selectedAddress.city}, ${selectedAddress.state} - ${selectedAddress.pincode}`,
      deliveryDate: deliveryDate,
      deliveryTimeSlot: deliveryTimeSlot,
      items: cartProducts,
      subtotal: subtotal,
      convenienceFee: convenienceFee,
      total: total,
      currency: currencyCode,
      paymentMethod: "razorpay",
      paymentStatus: "Pending",
      isTrial: false
    };
    
    setInvoiceData(invoiceData);
    setShowInvoice(true);
  };

  const handlePaymentSuccess = async (paymentData) => {
    try {
      // Process the order with delivery info
      const orderResult = await cartService.buyCart(customerId, {
        ...selectedAddress,
        deliveryDate,
        deliveryTimeSlot
      });
      
      // Clear cart state
      setCartProducts([]);
      
      // Update cart count in header
      const event = new CustomEvent('cartUpdated');
      window.dispatchEvent(event);
      
      // Update order status to paid if we have an order ID
      if (orderResult && orderResult.orderId) {
        try {
          await orderService.updateOrderStatus(orderResult.orderId, 'paid', paymentData?.paymentId);
        } catch (updateErr) {
          console.error("Error updating order status:", updateErr);
        }
      }
      
      // Show success message briefly before redirecting
      setTimeout(() => {
        setShowInvoice(false);
        navigate("/orders");
      }, 800);
    } catch (err) {
      console.error("Error processing payment:", err);
      alert("Order processing failed. Please try again.");
      // Close invoice and navigate to orders page even on error
      setShowInvoice(false);
      navigate("/orders");
    }
  };

  const handlePayment = async () => {
    setProcessing(true);
    try {
      // Prepare order data for Razorpay
      const orderData = {
        orderId: invoiceData.invoiceNumber,
        totalAmount: invoiceData.total, // Use actual cart total instead of fixed amount
        items: invoiceData.items
      };
      
      // Ensure we have a phone number from selected address
      const phone = selectedAddress.phone || sessionStorage.getItem("customerPhone") || "9999999999";
      // Save phone to session for future use
      if (selectedAddress.phone) {
        sessionStorage.setItem("customerPhone", selectedAddress.phone);
      }
      
      // Prepare customer info for Razorpay
      const customerInfo = {
        customerId: customerId,
        name: invoiceData.customerName || "Test Customer",
        email: sessionStorage.getItem("customerEmail") || "test@example.com",
        phone: phone,
        address: selectedAddress
      };
      
      console.log("Starting Razorpay payment with data:", { orderData, customerInfo });
      
      // Process payment with Razorpay
      paymentService.processRazorpayPayment(orderData, customerInfo)
        .then(result => {
          console.log("Payment result:", result);
          if (result.success) {
            // Update invoice data with success status immediately
            setInvoiceData(prevData => ({
              ...prevData,
              paymentStatus: 'Paid',
              paymentId: result.paymentId || 'RZP-' + Date.now(),
              paymentDate: new Date().toLocaleString()
            }));
            
            // Then process the order
            handlePaymentSuccess(result);
          } else {
            setProcessing(false);
            // Update invoice data with failed status
            setInvoiceData({
              ...invoiceData,
              paymentStatus: 'Failed',
              paymentId: result.paymentId || null
            });
          }
        })
        .catch(error => {
          console.error("Razorpay payment error:", error);
          setProcessing(false);
          if (error.error === 'Payment cancelled by user') {
            alert("Payment was cancelled.");
            // Update invoice data with pending status
            setInvoiceData({
              ...invoiceData,
              paymentStatus: 'Pending'
            });
          } else {
            // Update invoice data with failed status
            setInvoiceData({
              ...invoiceData,
              paymentStatus: 'Failed',
              paymentError: error.error || "Unknown error"
            });
          }
        });
    } catch (error) {
      console.error("Payment setup error:", error);
      setProcessing(false);
      // Update invoice data with failed status
      setInvoiceData({
        ...invoiceData,
        paymentStatus: 'Failed',
        paymentError: error.message || "Payment setup failed"
      });
    }
  };

  const handleBackToProducts = () => {
    navigate("/");
  };

  const handleLoginRedirect = () => {
    navigate("/login");
  };

  // Function to print the invoice
  const printInvoice = () => {
    const invoiceContent = invoiceRef.current;
    if (!invoiceContent) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write('<html><head><title>Invoice</title>');
    printWindow.document.write('<style>');
    printWindow.document.write(`
      body { font-family: Arial, sans-serif; padding: 20px; }
      .invoice { max-width: 800px; margin: 0 auto; padding: 20px; border: 1px solid #ccc; }
      .invoice-header { display: flex; justify-content: space-between; margin-bottom: 20px; }
      .invoice-details { text-align: right; }
      .logo-container { display: flex; align-items: center; margin-bottom: 15px; }
      .logo { height: 50px; margin-right: 15px; }
      .company-name { font-size: 24px; font-weight: bold; color: #133c0b; margin: 0; }
      .company-address { margin: 5px 0; font-size: 14px; }
      table { width: 100%; border-collapse: collapse; margin: 20px 0; }
      th, td { padding: 10px; text-align: left; border-bottom: 1px solid #ddd; }
      .total-label { text-align: right; font-weight: bold; }
      .total-amount { font-weight: bold; }
      .invoice-footer { margin-top: 30px; text-align: center; font-size: 14px; }
      @media print { .no-print { display: none; } }
    `);
    printWindow.document.write('</style></head><body>');
    
    // Clone the invoice content without payment options
    const clonedInvoice = invoiceContent.cloneNode(true);
    const paymentSection = clonedInvoice.querySelector('.payment-section');
    if (paymentSection) {
      const paymentMethodInfo = document.createElement('div');
      paymentMethodInfo.innerHTML = `<p><strong>Payment Method:</strong> Online Payment (Razorpay)</p>`;
      paymentSection.innerHTML = '';
      paymentSection.appendChild(paymentMethodInfo);
    }
    
    // Add logo to the invoice header
    const headerSection = clonedInvoice.querySelector('.invoice-header');
    if (headerSection) {
      const logoContainer = document.createElement('div');
      logoContainer.className = 'logo-container';
      logoContainer.innerHTML = `
        <img src="${brand}" alt="Organic Greens Hub Logo" class="logo" />
        <div>
          <p class="company-name">ORGANIC GREENS HUB</p>
          <p class="company-address">Plot No. 142, First Floor, Raghavendra Nagar, Jillelaguda</p>
          <p class="company-address">Hyderabad – 500097, Telangana, India</p>
          <p class="company-address">Phone: +91 9491657949</p>
        </div>
      `;
      headerSection.prepend(logoContainer);
    }
    
    printWindow.document.write(clonedInvoice.outerHTML);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  // Invoice component
  const InvoiceView = () => {
    // Skip rendering if currency symbol not available
    if (!currencySymbols[userCountry]) {
      return (
        <div className="invoice-container">
          <div className="invoice">
            <h1>Currency not supported</h1>
            <p>Sorry, we don't support your currency at this time.</p>
            <button onClick={() => setShowInvoice(false)}>Back to Cart</button>
          </div>
        </div>
      );
    }
    
    return (
      <div className="invoice-container">
        <div className="invoice" ref={invoiceRef}>
          <div className="invoice-header">
            <div className="logo-section">
              <img src={brand} alt="Organic Greens Hub Logo" className="invoice-logo" />
              <div className="company-info">
                <h2>ORGANIC GREENS HUB</h2>
                <p>Plot No. 142, First Floor, Raghavendra Nagar, Jillelaguda</p>
                <p>Hyderabad – 500097, Telangana, India</p>
                <p>Phone: +91 9491657949</p>
              </div>
            </div>
            <div className="invoice-details">
              <div>
                <strong>Invoice #:</strong> {invoiceData.invoiceNumber}
              </div>
              <div>
                <strong>Date:</strong> {invoiceData.date}
              </div>
            </div>
          </div>
          
          <div className="customer-details">
            <h2>Customer Information</h2>
            <div>
              <strong>Name:</strong> {invoiceData.customerName}
            </div>
            <div>
              <strong>Customer ID:</strong> {invoiceData.customerId}
            </div>
            <div>
              <strong>Shipping Address:</strong> {invoiceData.address}
            </div>
            <div>
              <strong>Delivery Date:</strong> {invoiceData.deliveryDate}
            </div>
            <div>
              <strong>Delivery Time:</strong> {invoiceData.deliveryTimeSlot}
            </div>
          </div>
          
          <div className="order-details">
            <h2>Order Details</h2>
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Quantity</th>
                  <th>Unit Price</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {invoiceData.items.map((item, index) => (
                  <tr key={index}>
                    <td>{item.name}</td>
                    <td>{item.quantity}</td>
                    <td>{formatPrice(item.price)}</td>
                    <td>{formatPrice(item.price * item.quantity)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="3" className="total-label">Subtotal</td>
                  <td className="total-amount">{formatPrice(invoiceData.subtotal)}</td>
                </tr>
                {invoiceData.convenienceFee > 0 && (
                  <tr>
                    <td colSpan="3" className="total-label">Convenience Fee</td>
                    <td className="total-amount">{formatPrice(invoiceData.convenienceFee)}</td>
                  </tr>
                )}
                <tr>
                  <td colSpan="3" className="total-label"><strong>Total</strong></td>
                  <td className="total-amount"><strong>{formatPrice(invoiceData.total)}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
          
          <div className="payment-section">
            <h2>Payment</h2>
            <div className="price-summary">
              <h3>Price Summary</h3>
              <p className="total-price">{formatPrice(invoiceData.total)}</p>
            </div>
            
            {invoiceData.paymentStatus === 'Paid' ? (
              <div className="payment-success">
                <div className="success-icon">✓</div>
                <h3>Payment Successful!</h3>
                <div className="payment-details">
                  <p><strong>Payment ID:</strong> {invoiceData.paymentId}</p>
                  <p><strong>Payment Date:</strong> {invoiceData.paymentDate}</p>
                  <p><strong>Status:</strong> {invoiceData.paymentStatus}</p>
                </div>
                <p className="redirect-message">Redirecting to orders page...</p>
              </div>
            ) : invoiceData.paymentStatus === 'free_trial' ? (
              <div className="payment-success" style={{ background: 'linear-gradient(135deg, #fff5f5 0%, #ffe8e8 100%)' }}>
                <div className="success-icon" style={{ background: '#ff6b6b' }}>🎁</div>
                <h3 style={{ color: '#ff6b6b' }}>Free Trial Order!</h3>
                <div className="payment-details">
                  <p><strong>Trial ID:</strong> {invoiceData.paymentId}</p>
                  <p><strong>Order Date:</strong> {invoiceData.paymentDate}</p>
                  <p><strong>Status:</strong> No Payment Required</p>
                  <p style={{ color: '#ff6b6b', fontWeight: 'bold', marginTop: '10px' }}>
                    ✅ Order placed using your free trial!
                  </p>
                </div>
                <p className="redirect-message">Redirecting to orders page...</p>
              </div>
            ) : invoiceData.paymentStatus === 'Pending' || invoiceData.paymentStatus === 'Failed' ? (
              <div className="payment-pending">
                <h3>{invoiceData.paymentStatus === 'Pending' ? 'Payment Pending' : 'Payment Failed'}</h3>
                <div className="payment-details">
                  {invoiceData.paymentId && <p><strong>Payment ID:</strong> {invoiceData.paymentId}</p>}
                  {invoiceData.paymentStatus === 'Failed' && <p className="payment-error">Your payment could not be processed. Please try again.</p>}
                </div>
                <div className="payment-buttons">
                  <button 
                    className="pay-button razorpay-button" 
                    onClick={handlePayment}
                    disabled={processing}
                  >
                    {processing ? 'Processing...' : 'Pay with Razorpay'}
                  </button>
                  <button className="cancel-button" onClick={() => setShowInvoice(false)} disabled={processing}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="payment-options">
                <div className="payment-buttons">
                  <button 
                    className="pay-button razorpay-button" 
                    onClick={handlePayment}
                    disabled={processing}
                    style={invoiceData.isTrial ? {
                      background: 'linear-gradient(135deg, #ff6b6b, #ee5a6f)',
                      fontSize: '18px'
                    } : {}}
                  >
                    {processing ? 'Processing...' : (invoiceData.isTrial ? '🎁 Place Free Order' : 'Pay with Razorpay')}
                  </button>
                  <button className="cancel-button" onClick={() => setShowInvoice(false)} disabled={processing}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <div className="invoice-footer">
            <p><strong>Payment Method:</strong> Online Payment (Razorpay)</p>
            <p>Thank you for shopping with Organic Greens Hub!</p>
            <p>For any questions, please contact us at +91 9491657949</p>
          </div>
        </div>
      </div>
    );
  };

  if (!isAuthenticated) {
    return (
      <div className="shopping-cart-container">
        <h1>Shopping Cart</h1>
        <div className="login-required">
          <p>Please log in to view your cart and complete your purchase.</p>
          <button onClick={handleLoginRedirect}>Login</button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="shopping-cart-container">
        <h1>Shopping Cart</h1>
        <div className="loading">Loading your cart...</div>
      </div>
    );
  }

  const handleCheckout = () => {
    setDateError("");
    if (addresses.length === 0) {
      alert('Please add at least one address in your profile before proceeding.');
      const event = new CustomEvent('openProfileModal');
      window.dispatchEvent(event);
      return;
    }
    const selectedDate = new Date(deliveryDate);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);
    if (selectedDate < tomorrow) {
      setDateError('Please select a delivery date from tomorrow onwards.');
      return;
    }
    generateInvoice();
  };

  if (showInvoice) {
    return <InvoiceView />;
  }

  // Skip rendering if currency symbol not available
  if (!currencySymbols[userCountry]) {
    return (
      <div className="shopping-cart-container">
        <h1>Shopping Cart</h1>
        <div className="error-message">
          <p>Sorry, we don't support your currency at this time.</p>
          <button onClick={handleBackToProducts}>Back to Products</button>
        </div>
      </div>
    );
  }

  return (
    <div className="shopping-cart-container">
      <h1>Shopping Cart</h1>
      
      {standalone && (
        <button className="back-button" onClick={handleBackToProducts}>
          Back to Products
        </button>
      )}
      
      {cartProducts?.length > 0 ? (
        <>
          <div className="cart">
            <table className="shopping-cart-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Price</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {cartProducts.map((product) => (
                  <tr key={product.productId}>
                    <td>{product.name}</td>
                    <td>{product.quantity} X {formatPrice(product.price)} = {formatPrice(product.price * product.quantity)}</td>
                    <td>
                      <button
                        onClick={() =>
                          removeProduct(product.productId)
                        }
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="grand-total">
              <strong>Total: {formatPrice(cartProducts.reduce((sum, item) => sum + item.price * item.quantity, 0))}</strong>
            </div>
            
            <div className="address-section">
              <h3>Shipping Address</h3>
              {savedAddress ? (
                <div className="selected-address">
                  <p><strong>Selected Address:</strong></p>
                  <div className="address-display">
                    <p>{savedAddress}</p>
                    <button 
                      className="change-address-btn" 
                      onClick={() => setShowAddressSelector(true)}
                    >
                      Change Address
                    </button>
                  </div>
                </div>
              ) : (
                <div className="no-address">
                  <p>No address selected</p>
                  <button 
                    className="select-address-btn" 
                    onClick={() => setShowAddressSelector(true)}
                  >
                    Select Address
                  </button>
                </div>
              )}
              
              {pinCodeError && (
                <div className="error-message" style={{color: '#d32f2f', fontSize: '14px', marginTop: '10px'}}>
                  {pinCodeError}
                </div>
              )}
              {dateError && (
                <div className="error-message" style={{color: '#d32f2f', fontSize: '14px', marginTop: '10px', padding: '12px', backgroundColor: '#ffebee', borderRadius: '4px', borderLeft: '4px solid #d32f2f'}}>
                  {dateError}
                </div>
              )}
              
              <div className="delivery-info">
                <h4>Delivery Information</h4>
                <div className="delivery-date">
                  <label>Delivery Date *</label>
                  <input
                    type="date"
                    value={deliveryDate}
                    onChange={(e) => {
                      setDeliveryDate(e.target.value);
                      setDateError("");
                    }}
                    min={new Date(new Date().getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                    className="form-input"
                    required
                  />
                </div>
                <div className="time-slot-selection">
                  <label>Select Delivery Time Slot *</label>
                  <select 
                    value={deliveryTimeSlot} 
                    onChange={(e) => setDeliveryTimeSlot(e.target.value)}
                    className="form-input"
                    required
                  >
                    <option value="">Choose time slot</option>
                    <option value="8:00 AM - 9:00 AM">Morning (8:00 AM - 9:00 AM)</option>
                    <option value="9:00 AM - 10:00 AM">Morning (9:00 AM - 10:00 AM)</option>
                  </select>
                </div>
              </div>
              
              <div className="address-actions">
                <button 
                  className="buy-button" 
                  onClick={handleCheckout}
                  disabled={!selectedAddress || selectedAddress.pincode !== '500019' || !deliveryTimeSlot}
                >
                  Proceed to Checkout
                </button>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="empty-cart">
          <p>Your cart is empty</p>
          <button onClick={handleBackToProducts}>Continue Shopping</button>
        </div>
      )}
      
      {/* Address Selector Modal */}
      {showAddressSelector && (
        <div className="address-selector-overlay">
          <div className="address-selector-modal">
            <div className="selector-header">
              <h3>Select Delivery Address</h3>
              <button 
                className="close-btn" 
                onClick={() => setShowAddressSelector(false)}
                style={{
                  position: 'absolute',
                  right: '20px',
                  top: '20px',
                  background: 'none',
                  border: 'none',
                  fontSize: '32px',
                  cursor: 'pointer',
                  color: '#6c757d',
                  padding: '0',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
            <div className="addresses-list">
              {addresses.length > 0 ? (
                addresses.map(address => (
                  <div 
                    key={address.id} 
                    className={`address-option ${selectedAddress?.id === address.id ? 'selected' : ''}`}
                    onClick={() => handleAddressSelect(address)}
                  >
                    <div className="address-content">
                      <div className="address-header">
                        <strong>{address.fullName}</strong>
                        {address.isDefault && <span className="default-badge">Default</span>}
                      </div>
                      <p>{address.addressLine1}</p>
                      {address.addressLine2 && <p>{address.addressLine2}</p>}
                      <p>{address.city}, {address.state} - {address.pincode}</p>
                      <p>Phone: {address.phone}</p>
                    </div>
                    {address.pincode !== '500019' && (
                      <div className="pincode-warning">
                        <small>⚠️ Delivery not available to this pincode</small>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="no-addresses">
                  <p>No addresses found. Please add an address in your profile.</p>
                  <button 
                    className="add-address-btn"
                    onClick={() => {
                      setShowAddressSelector(false);
                      const event = new CustomEvent('openProfileModal');
                      window.dispatchEvent(event);
                    }}
                  >
                    Go to Profile
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShoppingCart;